import React, { useEffect, useRef, useState } from "react";
import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import "@xterm/xterm/css/xterm.css";
import { useNavigate } from "react-router-dom";

import { parseUtc } from "../utils/datetime";
import api from "../api/client";
import ActionButton from "../components/ActionButton";

type Status = "idle" | "creating" | "connecting" | "active" | "closed" | "error";

/** Which terminal session belongs to THIS TAB.
 *
 * sessionStorage, not localStorage, and the distinction is the whole point:
 * it is per-tab and survives navigation and reload within that tab, which is
 * exactly the scope of "the terminal I was just using". Two tabs get two
 * sessions and neither steals the other's screen. */
const TAB_SESSION_KEY = "aerogauge.aiTerminal.session";
/* The server boot time this tab last saw. Sessions live only inside the
   server process, so a change here is the reason a remembered session has
   vanished - and it is the one cause the operator could never see. */
const TAB_BOOT_KEY = "aerogauge.aiTerminal.serverBoot";

// Mirrors the DeviceShell page's xterm lifecycle, but pointed at the
// appliance-local AI terminal endpoint. Server spawns a fresh bash PTY
// inside a pre-staged workspace with CLAUDE.md describing the RMM
// context — the operator can then run Claude (or any other CLI) and
// it will pick that up automatically.
export default function AITerminal() {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const termRef = useRef<Terminal | null>(null);
  const fitRef = useRef<FitAddon | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  // The session token as a REF, not just state: the unmount cleanup closes over
  // the first render's state value, so releasing the session on the way out
  // needs a mutable handle to whatever token is actually live.
  const tokenRef = useRef<string | null>(null);
  // "New session" must mean NEW. Mount reattaches to a live session by
  // default, so without this the button would hand back a session the user
  // just asked to be rid of.
  const forceNewRef = useRef(false);

  // Why the remembered session is gone, if it is. Written into the terminal
  // once it is up, so the explanation lands where the operator is looking
  // rather than in a banner above it.
  const lostReasonRef = useRef<string | null>(null);

  const [status, setStatus] = useState<Status>("idle");
  const [token, setToken] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [sessionKey, setSessionKey] = useState(0); // bump to reconnect
  // True when this page attached to a session that was already running, so
  // the banner can say so rather than leaving the operator wondering whether
  // the scrollback they are looking at is live.
  const [resumed, setResumed] = useState(false);

  // Setup whenever sessionKey changes (initial mount = 0; "New session" bumps it).
  useEffect(() => {
    let cancelled = false;
    let term: Terminal | null = null;
    let ws: WebSocket | null = null;

    const sendResize = () => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
      const t = termRef.current;
      if (!t) return;
      try {
        wsRef.current.send(JSON.stringify({ type: "resize", cols: t.cols, rows: t.rows }));
      } catch {
        /* ignore */
      }
    };

    // Defer fit() to after the browser has laid out the container.
    // Without this, xterm's Viewport reads dimensions before its
    // renderer has finished initialising and throws
    // "Cannot read properties of undefined (reading 'dimensions')".
    // rAF schedules the call after the next paint, by which time the
    // flex parent has measured the container.
    const safeFit = () => {
      const el = containerRef.current;
      const fit = fitRef.current;
      if (!fit || !el || el.clientWidth === 0 || el.clientHeight === 0) return;
      try {
        fit.fit();
      } catch {
        /* xterm hasn't finished mounting — try again on the next frame */
        requestAnimationFrame(safeFit);
      }
    };

    const setup = async () => {
      setStatus("creating");
      setError("");
      term = new Terminal({
        convertEol: true,
        cursorBlink: true,
        fontFamily: 'Consolas, "Cascadia Mono", "JetBrains Mono", "Liberation Mono", monospace',
        fontSize: 14,
        theme: { background: "#0a0e1a", foreground: "#e6edf3" },
        scrollback: 5000,
      });
      const fit = new FitAddon();
      term.loadAddon(fit);
      termRef.current = term;
      fitRef.current = fit;
      if (containerRef.current) {
        term.open(containerRef.current);
        // Wait for layout + paint before fitting / writing. Two
        // requestAnimationFrame ticks: one to flush layout, one for
        // xterm's own renderer init that happens during the first paint.
        requestAnimationFrame(() => requestAnimationFrame(safeFit));
      }

      let tok: string;
      let ws_url: string;
      let ws_path: string;
      const createSession = async () => api.post(`/api/v1/ai-terminal/sessions`, {});
      try {
        let r;
        try {
          // REATTACH to a session of ours that is still alive, in preference
          // to opening another one.
          //
          // Requested: "it would be beneficial to navigate between sessions
          // without losing the current connection to Claude... the last thing
          // you want is to accidentally click on a feature and then return to
          // the terminal only to find the session has ended."
          //
          // The PTY outlives the socket, and the server replays the trailing
          // output on attach, so coming back to this page picks the
          // conversation up where it was left. This also removes the reason
          // unmount used to DELETE the session: sessions are now reused
          // rather than accumulated, so the per-user cap of 3 is not spent by
          // three visits to the page.
          //
          // WHICH session, and why it is remembered per TAB.
          //
          // The first attempt picked the newest session with ws_connected
          // false, and it did not work: a browser navigation can close the
          // TCP connection without the server noticing for several seconds,
          // so a page coming straight back saw its own session still flagged
          // as connected, skipped it, and opened a second one. Measured, not
          // theorised — two sessions sixteen seconds apart.
          //
          // sessionStorage is exactly the right scope: per tab, and it
          // survives navigation and reload within that tab. So "my session"
          // is a fact this tab knows rather than something to infer from
          // flags that lag. A second tab has its own storage and therefore
          // its own session — nobody's screen gets yanked away.
          //
          // The ws_connected scan stays as the fallback for when there is
          // nothing remembered (a new tab, or storage cleared), where being
          // conservative is right.
          const listed = forceNewRef.current
            ? null
            : await api
                .get(`/api/v1/ai-terminal/sessions`)
                .then((x) => x.data)
                .catch(() => null);
          const mine = listed?.sessions || [];
          const bootNow: string | null = listed?.server_started_at || null;
          const remembered = forceNewRef.current
            ? null
            : sessionStorage.getItem(TAB_SESSION_KEY);
          const bootThen = sessionStorage.getItem(TAB_BOOT_KEY);
          forceNewRef.current = false;
          const live =
            mine.find((x: any) => x.token === remembered) ||
            mine
              .filter((x: any) => !x.ws_connected)
              .sort((a: any, b: any) => (a.last_activity > b.last_activity ? -1 : 1))[0];

          // SAY WHY THE SESSION IS GONE.
          //
          // Reported as "switching between screens closes the session". The
          // detach and reattach were measured working — a session with no
          // socket is still listed and still has 30 minutes on its idle
          // clock. What actually ended them was the server RESTARTING in
          // between: sessions live only in that process and the PTY is a
          // child of it, so a restart kills the shells outright and no
          // reattach can survive it.
          //
          // From the chair those are identical: you come back and get an
          // empty terminal. Naming the cause is the difference between a
          // product that lost your work and one that told you the server
          // was restarted underneath you.
          if (remembered && !mine.some((x: any) => x.token === remembered)) {
            if (bootThen && bootNow && bootThen !== bootNow) {
              const when = parseUtc(bootNow);
              lostReasonRef.current =
                `Your previous session ended because the AeroGauge server restarted` +
                (when ? ` at ${when.toLocaleTimeString()}` : "") +
                `. Terminal sessions run inside the server process, so a restart ends them.`;
            } else {
              lostReasonRef.current =
                "Your previous session is no longer available — it may have been idle for " +
                "more than 30 minutes.";
            }
          }
          if (bootNow) sessionStorage.setItem(TAB_BOOT_KEY, bootNow);
          if (live?.token) {
            // Only CLAIM a resume when there is a shell behind the token. A
            // session POSTed but never attached has no PTY and nothing to
            // restore; reusing it still saves a cap slot, but saying
            // "resumed" about a blank shell would be a lie.
            setResumed(!!live.has_pty);
            r = { data: { token: live.token, ws_url: `/api/v1/ai-terminal/ws/${live.token}` } };
          } else {
            setResumed(false);
            r = await createSession();
          }
        } catch (err: any) {
          // 429 = per-user session cap. Rather than dead-ending the user with
          // "close an existing session" — when the stale sessions are almost
          // always orphans from earlier visits, not windows they can see —
          // reclaim the oldest and retry once.
          if (err.response?.status !== 429) throw err;
          const list = await api.get(`/api/v1/ai-terminal/sessions`);
          const stale = (list.data?.sessions || [])
            .filter((x: any) => !x.ws_connected)
            .sort((a: any, b: any) => (a.last_activity < b.last_activity ? -1 : 1));
          if (!stale.length) throw err;
          await api.delete(`/api/v1/ai-terminal/sessions/${stale[0].token}`).catch(() => {});
          r = await createSession();
        }
        tok = r.data.token;
        ws_path = r.data.ws_url; // e.g. /api/v1/ai-terminal/ws/<token>
        tokenRef.current = tok;
        sessionStorage.setItem(TAB_SESSION_KEY, tok); // this tab's session
        setToken(tok);
      } catch (err: any) {
        if (cancelled) return;
        const msg = err.response?.data?.detail || err.message;
        term.writeln(`\x1b[31m[aerogauge] Failed to create session: ${msg}\x1b[0m`);
        setError(msg);
        setStatus("error");
        return;
      }
      if (cancelled) return;
      setStatus("connecting");

      const jwt = localStorage.getItem("token") || "";
      const wsProto = window.location.protocol === "https:" ? "wss:" : "ws:";
      ws_url = `${wsProto}//${window.location.host}${ws_path}?access_token=${encodeURIComponent(jwt)}`;
      ws = new WebSocket(ws_url);
      ws.binaryType = "arraybuffer";
      wsRef.current = ws;

      ws.onopen = () => {
        if (cancelled) return;
        setStatus("active");
        // If the session this tab remembered is gone, say why — here, in
        // the terminal, before the new shell's first prompt. An empty
        // terminal with no explanation is what "it closed when I switched
        // screens" actually looked like from the operator's side.
        if (lostReasonRef.current) {
          term?.writeln(`\x1b[33m[aerogauge] ${lostReasonRef.current}\x1b[0m`);
          term?.writeln("\x1b[90m[aerogauge] This is a new session.\x1b[0m");
          lostReasonRef.current = null;
        }
        term?.focus();
        // Tell the server-side PTY about our viewport so the welcome
        // banner + prompt render at the right width.
        sendResize();
      };

      ws.onmessage = (evt) => {
        if (!term) return;
        if (evt.data instanceof ArrayBuffer) {
          term.write(new Uint8Array(evt.data));
        } else if (typeof evt.data === "string") {
          term.write(evt.data);
        }
      };

      ws.onerror = () => {
        if (cancelled) return;
        term?.writeln("\r\n\x1b[31m[aerogauge] WebSocket error.\x1b[0m");
        setStatus("error");
      };

      ws.onclose = (evt) => {
        if (cancelled) return;
        const reason = evt.reason || `code ${evt.code}`;
        term?.writeln(`\r\n\x1b[90m[aerogauge] Disconnected (${reason}).\x1b[0m`);
        setStatus("closed");
      };

      const enc = new TextEncoder();
      term.onData((data) => {
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(enc.encode(data));
        }
      });
    };

    setup();

    let resizeTimer: number | undefined;
    const onResize = () => {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        safeFit();
        sendResize();
      }, 150);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelled = true;
      window.removeEventListener("resize", onResize);
      // DETACH ONLY. Leaving the page must not end the conversation.
      //
      // This used to DELETE the server session, because the PTY outlived the
      // socket and three visits inside half an hour exhausted the per-user
      // cap of 3. That is now handled the right way round: mount REATTACHES
      // to a live session instead of opening another, so they are reused
      // rather than accumulated, and nothing needs destroying on the way out.
      //
      // The 30-minute idle reaper is still the backstop for a session nobody
      // returns to, and "End & close" ends one deliberately.
      try {
        ws?.close();
      } catch {
        /* ignore */
      }
      try {
        term?.dispose();
      } catch {
        /* ignore */
      }
      wsRef.current = null;
      termRef.current = null;
      fitRef.current = null;
    };
  }, [sessionKey]);

  const newSession = async () => {
    forceNewRef.current = true; // skip the reattach — the point is a clean one
    sessionStorage.removeItem(TAB_SESSION_KEY);
    setResumed(false);
    // Close current first (best-effort; PTY also gets reaped by idle).
    if (token) {
      try {
        await api.delete(`/api/v1/ai-terminal/sessions/${token}`);
      } catch {
        /* ignore */
      }
    }
    setSessionKey((k) => k + 1);
  };

  // "Close" ends the session AND leaves the page.
  //
  // It used to only kill the socket, which left you looking at a dead black
  // rectangle and a "Disconnected" badge on a page whose only other control was
  // "New session" — the button's sole effect was to break the thing in front of
  // you. Now that unmounting releases the session on its own, the only reading
  // of "Close" that means anything is: end this and take me out of here.
  const closeSession = async () => {
    const t = tokenRef.current;
    tokenRef.current = null; // stop the unmount cleanup double-deleting
    sessionStorage.removeItem(TAB_SESSION_KEY); // do not resume what was ended
    if (t) {
      try {
        await api.delete(`/api/v1/ai-terminal/sessions/${t}`);
      } catch {
        /* ignore — the idle reaper is the backstop */
      }
    }
    try {
      wsRef.current?.close();
    } catch {
      /* ignore */
    }
    navigate(-1);
  };

  const badge = (() => {
    const map: Record<Status, { label: string; cls: string }> = {
      idle: { label: "Idle", cls: "bg-gray-100 text-gray-700 dark:bg-gray-700/40 dark:text-gray-300" },
      creating: { label: "Creating session…", cls: "bg-gray-100 text-gray-700 dark:bg-gray-700/40 dark:text-gray-300" },
      connecting: { label: "Connecting…", cls: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300" },
      active: { label: "Connected", cls: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" },
      closed: { label: "Disconnected", cls: "bg-gray-200 text-gray-700 dark:bg-gray-700/60 dark:text-gray-300" },
      error: { label: "Error", cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" },
    };
    const s = map[status];
    return <span className={`text-xs px-2 py-1 rounded-full font-medium ${s.cls}`}>{s.label}</span>;
  })();

  return (
    <div className="flex flex-col h-full">
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 mb-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">AI Terminal</h1>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            A shell on the RMM appliance, pre-connected to this AeroGauge instance: the AI CLI comes
            up authenticated with an API token scoped to you, so it can query customers, devices,
            tickets and alerts directly.{" "}
            {/* Say plainly that leaving is safe. The worry reported was
                "accidentally click on a feature and then return to the
                terminal only to find the session has ended" — which is a
                worry about what the page does, so the page should answer
                it. */}
            <span className="font-medium text-gray-600 dark:text-gray-300">
              Leaving this page keeps the session running
            </span>{" "}
            — come back and you pick up where you left off. Sessions with no activity for
            30&nbsp;min are closed automatically; “End &amp; close” ends one now.
            {/* The workspace path used to print here. It is a 32-char session
                token on the end of an appliance path — noise to the operator,
                and it exposed internal layout on a screen they look at daily.
                Still available via `pwd` inside the shell, where it belongs. */}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {resumed && (
            <span
              className="text-xs px-2 py-1 rounded-full font-medium bg-brand/10 text-brand dark:bg-brand-light/15 dark:text-brand-light"
              title="This terminal was still running from earlier — its scrollback and working directory are intact"
            >
              Resumed
            </span>
          )}
          {badge}
          <ActionButton onAction={() => newSession()} variant="raw" className="text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700" pendingLabel="Working…">
            New session
          </ActionButton>
          <ActionButton onAction={() => closeSession()} variant="raw" className="text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700" title="End this session and leave the AI Terminal" pendingLabel="Closing…">
            End &amp; close
          </ActionButton>
        </div>
      </div>
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg px-4 py-2 mb-3 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}
      <div
        ref={containerRef}
        className="flex-1 rounded-xl shadow-sm overflow-x-auto"
        style={{ backgroundColor: "#0a0e1a", minHeight: "60vh", padding: "10px" }}
      />
    </div>
  );
}
